import type { Editor } from '@tiptap/core';
import { ANALYSIS_WORD_LIMIT } from '../constants';
import { MIN_FIRST_CHAPTER_WORDS } from './first-chapter';
import { countWords } from './word-count';
import { getDocumentPlainText } from './document-plain-text';

export const MIN_ANALYZE_WORD_COUNT = 20;

export type FeedbackAnalyzeDisableReason =
  | 'too_short_one_word'
  | 'too_short'
  | 'selection_too_long'
  | 'document_too_long'
  | 'section_too_short'
  | 'section_too_long'
  | null;

type FeedbackAnalyzeAvailabilityOptions = {
  allowDocumentFallback?: boolean;
};

export function getFeedbackAnalyzeDisableReasonFromWordCounts(
  hasSelection: boolean,
  wordCount: number,
  options: FeedbackAnalyzeAvailabilityOptions = {}
): FeedbackAnalyzeDisableReason {
  if (hasSelection) {
    if (wordCount < MIN_ANALYZE_WORD_COUNT) {
      return wordCount === 1 ? 'too_short_one_word' : 'too_short';
    }
    if (wordCount > ANALYSIS_WORD_LIMIT) {
      return 'selection_too_long';
    }
    return null;
  }

  if (!options.allowDocumentFallback) {
    return 'too_short';
  }

  if (wordCount > ANALYSIS_WORD_LIMIT) {
    return 'document_too_long';
  }

  return null;
}

export function getFeedbackAnalyzeDisableReason(
  editor: Editor | null | undefined,
  options: FeedbackAnalyzeAvailabilityOptions = {}
): FeedbackAnalyzeDisableReason {
  if (!editor) {
    return null;
  }

  const { from, to } = editor.state.selection;
  const hasSelection = from !== to;

  if (hasSelection) {
    const selectedText = getDocumentPlainText(editor.state.doc, from, to, ' ');
    return getFeedbackAnalyzeDisableReasonFromWordCounts(true, countWords(selectedText), options);
  }

  const documentText = getDocumentPlainText(editor.state.doc);
  return getFeedbackAnalyzeDisableReasonFromWordCounts(false, countWords(documentText), options);
}

export function getSectionAnalyzeDisableReason(wordCount: number): FeedbackAnalyzeDisableReason {
  if (wordCount < MIN_ANALYZE_WORD_COUNT) {
    return 'section_too_short';
  }
  if (wordCount > ANALYSIS_WORD_LIMIT) {
    return 'section_too_long';
  }
  return null;
}

export function isDocumentTooShortToAnalyze(wordCount: number | null): boolean {
  if (wordCount === null) return true;
  return wordCount < MIN_FIRST_CHAPTER_WORDS;
}

export function getDocumentAnalyzeTooShortTooltip(t: (key: string) => string): string {
  return t('analyze_document_too_short').replace(
    '{count}',
    MIN_FIRST_CHAPTER_WORDS.toLocaleString()
  );
}

export function getFeedbackAnalyzeDisableTooltip(
  reason: FeedbackAnalyzeDisableReason,
  t: (key: string) => string
): string | undefined {
  if (!reason) {
    return undefined;
  }

  const count = ANALYSIS_WORD_LIMIT.toLocaleString();

  switch (reason) {
    case 'too_short_one_word':
      return t('analysis_only_works_for_texts');
    case 'too_short':
      return t('analysis_only_works_for_longer_texts');
    case 'selection_too_long':
      return t('analysis_selection_too_long').replace('{count}', count);
    case 'document_too_long':
      return t('analysis_document_too_long').replace('{count}', count);
    case 'section_too_short':
      return t('analysis_section_too_short').replace(
        '{count}',
        MIN_ANALYZE_WORD_COUNT.toLocaleString()
      );
    case 'section_too_long':
      return t('analysis_section_too_long').replace('{count}', count);
  }
}
